import { formatInternalDate } from '../utils/time_utils';
import { BUDGET_TYPE_HOURS, BUDGET_TYPE_COST } from '~/app_constants';
import { get, pick, isString, isNil, omit, isEqual, every, some } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import Vue from 'vue';

export const handleBudget = (hasBudget, data, budget) => {
  return hasBudget && data && !isNil(budget?.type);
};

// state can be project.budget or project when formatting a members limit record
export const formatBudgetUpdate = (state, payload) => {
  const { value, path } = payload;

  if (path === 'members_limits.add') {
    Vue.set(state.members_limits, 'groups', [...state.members_limits.groups, initialMemberLimitBudgetState()]);
  } else if (path === 'members_limits.remove') {
    if (state.members_limits.groups[value].new_record) {
      // The limit is not persisted yet, it is just cleared
      if (state.members_limits.groups.length === 1) {
        // If it is the only group, reset the state
        Vue.set(state.members_limits, 'groups', [initialMemberLimitBudgetState()]);
      } else {
        Vue.set(
          state.members_limits,
          'groups',
          state.members_limits.groups.filter((_, index) => index !== value),
        );
      }
    } else {
      // Removing an existing limit
      // Store the limits to be removed in a separate array
      const toRemove = { ...state.members_limits.groups[value], _destroy: '1' };
      const removeGroup = state.members_limits.remove || [];

      Vue.set(state.members_limits, 'remove', [...removeGroup, toRemove]);

      // Remove the limit from the groups array
      const remainingLimits = state.members_limits.groups.filter((_, index) => index !== value);

      Vue.set(state.members_limits, 'groups', remainingLimits);

      if (!state.members_limits.groups.length) {
        // When removing the last group, reset the state
        Vue.set(state.members_limits, 'groups', [initialMemberLimitBudgetState()]);
      }
    }
  } else if (path.includes('members_limits.groups')) {
    const [, membersLimitPath, fieldPath] = /(members_limits\.groups\.\d+)\.(\w+)/.exec(path);

    formatBudgetUpdate(get(state, membersLimitPath), { value, path: fieldPath });
  } else if (path.includes('type')) {
    if (value === BUDGET_TYPE_HOURS.toString()) {
      Vue.set(state, 'rate', '0');
      Vue.set(state, 'hours_display', null);
    } else if (isNil(value)) {
      Vue.set(state, 'rate', null);
      Vue.set(state, 'cost', null);
      Vue.set(state, 'hours', null);
      Vue.set(state, 'hours_display', null);
    }
  } else if (path === 'members_limits.alert.threshold_percentage') {
    Vue.set(state.members_limits.alert, 'threshold_percentage', value);
  } else if (path === 'members_limits.alert.notify_members') {
    Vue.set(state.members_limits.alert, 'notify_members', value);
  } else if (path.includes('near_limit')) {
    Vue.set(state, 'notification', value);
  } else if (path.includes('recurrence_start')) {
    Vue.set(state, 'recurrence_start', value);
  } else if (path.includes('recurrence')) {
    Vue.set(state, 'recurrence', value);
  } else if (path === 'budget.alert.all_members') {
    Vue.set(state.alert, 'all_members', value);
  } else if (['budget.start', 'start'].includes(path)) {
    Vue.set(state, 'start_date', value);
  } else if (path.includes('stop_timer.limit')) {
    Vue.set(state, 'stop_at', value);
  }
};

export const formatBudgetInitial = (budgetState) => {
  const startDate = budgetState.start;

  if (startDate) {
    Vue.set(budgetState, 'start', moment(startDate));
    Vue.set(budgetState, 'start_date', startDate);
  } else {
    Vue.set(budgetState, 'start', null);
    Vue.set(budgetState, 'start_date', null);
  }

  if (budgetState.type === BUDGET_TYPE_COST) {
    Vue.set(budgetState, 'hours', null);
  } else if (budgetState.type === BUDGET_TYPE_HOURS) {
    Vue.set(budgetState, 'cost', null);
  } else {
    Vue.set(budgetState, 'cost', null);
    Vue.set(budgetState, 'hours', null);
  }

  if (budgetState.hours !== null) {
    Vue.set(budgetState, 'hours_display', Math.round(budgetState.hours * 60) * 60);
  }

  return budgetState;
};

export const initialBudgetState = () => {
  return {
    type: null,
    rate: null,
    hours: null,
    hours_display: null,
    cost: null,
    alert: { near_limit: null, all_members: false, notify_group: null, notify_enabled: false },
    notification: null,
    stop_timer: { limit: null, when_budget_reached: false },
    stop_at: null,
    recurrence: '',
    include_non_billable: true,
    start: null,
    start_date: null,
  };
};

export const BUDGET_FORM_KEYS = [
  'type',
  'rate',
  'cost',
  'start',
  'alert',
  'recurrence',
  'hours_display',
  'include_non_billable',
];

// Only get Object values, without functions
const objectValues = (object) => JSON.parse(JSON.stringify(object));

export const serializeMembersLimitsData = (membersLimits) => {
  if (!membersLimits) {
    return {};
  }
  const allowedKeys = [
    'type',
    'rate',
    'cost',
    'hours',
    'recurrence',
    'recurrence_start',
    'start',
    'start_date',
    'members',
  ];
  const excludedKeys = ['id', 'uuid', '_destroy'];
  const initialMemberLimitBudget = omit(initialMemberLimitBudgetState(), excludedKeys);
  const filteredGroups = Array.from(membersLimits.groups || []).filter((group) => {
    // Exclude from submission member limit budget with the initial state
    return !isEqual(objectValues(initialMemberLimitBudget), objectValues(omit(group, excludedKeys)));
  });

  // Add the removed member limit budgets to the submission
  if (membersLimits.remove) {
    filteredGroups.push(...membersLimits.remove);
  }

  if (!filteredGroups.length) {
    return { members_limits: {} };
  }

  return {
    members_limits: {
      alert: membersLimits.alert,
      groups: filteredGroups.map((membersLimit) => {
        const limit = serializeMembersLimitObject(membersLimit);
        const { id, _destroy, members, ...configBudget } = limit;
        const filteredConfigBudget = pick(configBudget, allowedKeys);

        return _destroy ? { id, _destroy } : { id, members, budget_attributes: filteredConfigBudget };
      }),
    },
  };
};

const serializeMembersLimitObject = (limit) => {
  if (every(['cost', 'hours', 'type'], (attr) => isNil(limit[attr]))) {
    limit._destroy = '1';
  }

  return serializeBudgetData(limit);
};

export const serializeBudgetData = (budget) => {
  const serialized = { ...budget };

  if (!budget) {
    return {};
  }

  if (budget.alert) {
    serialized.notification = budget.alert.near_limit;
    serialized.notify_enabled = budget.alert.notify_enabled;
    serialized.notify_group = budget.alert.notify_group;
  }

  // In some cases rate is not being set but it is a required field, so it is a workaround until the origin of the problem is found.
  if (isNil(serialized.rate) && some(['cost', 'hours', 'type'], (attr) => !isNil(budget[attr]))) {
    serialized.rate = 0;
  }

  if (budget.stop_timer) {
    serialized.stop_at = budget.stop_timer.limit;
    serialized.stop_timer_when_budget_reached = budget.stop_timer.when_budget_reached;
  }

  budget.hours_display && (serialized.hours = budget.hours_display / 60 / 60);

  !isString(budget.start_date) && (serialized.start_date = formatInternalDate(budget.start_date));

  return serialized;
};

export const initialMemberLimitBudgetState = () => ({
  id: null,
  uuid: uuidv4(),
  type: null,
  rate: null,
  hours: null,
  cost: null,
  recurrence: null,
  recurrence_start: 1,
  start: null,
  start_date: null,
  members: [],
  new_record: true,
});

export const initialMemberLimitNotificationsState = () => ({ notify_members: true, threshold_percentage: 80 });
